/**
 * PermissionsContext
 * Sistema de Control de Acceso Basado en Roles (RBAC) para cumplimiento HIPAA.
 *
 * HIPAA Requirement: 45 CFR 164.308(a)(4) - Information Access Management
 *
 * ============================================================================
 * DOCUMENTACIÓN PARA REFACTORIZACIÓN CON BACKEND
 * ============================================================================
 *
 * ESTADO ACTUAL (Frontend Only):
 * - Los roles y permisos están definidos en este archivo
 * - El rol del usuario viene de user.rol del login
 * - Si no hay rol definido, se asigna 'guest' con permisos mínimos
 *
 * CAMBIOS REQUERIDOS EN BACKEND:
 * 1. Agregar campo 'role' al JWT con el rol del usuario
 * 2. Crear endpoint GET /api/permissions para obtener permisos del usuario
 * 3. Crear endpoint GET /api/roles para obtener lista de roles disponibles
 * 4. Implementar validación de permisos en cada endpoint de API
 *
 * REFACTORIZACIÓN FUTURA:
 * 1. Obtener permisos del backend en lugar de definirlos aquí
 * 2. Sincronizar permisos con el backend en cada request
 * 3. Implementar cache de permisos con invalidación
 * 4. Agregar permisos granulares por paciente/location
 *
 * ============================================================================
 */

import React, { createContext, useContext, useMemo, useCallback } from "react";
import { AuthContext } from "../AuthContext/AuthContext";

export const PermissionsContext = createContext();

/**
 * Definición de Roles del Sistema
 * TODO: Mover a backend y obtener via API
 */
export const ROLES = {
  ADMIN: "admin",
  PCP: "pcp",
  CARE_MANAGEMENT: "care_management",
  CEO: "ceo",
  BEHAVIORAL_HEALTH: "behavioral_health",
  GUEST: "guest",
};

/**
 * Mapeo de roles del backend a roles del sistema.
 *
 * FUENTE DE VERDAD: el `roleKey` (string estable que envía el backend en
 * employee-info y en el claim `role_key` del JWT). Las entradas numéricas son
 * SOLO un fallback legacy y NO son confiables: los IDs de rol los asigna la DB
 * de forma autoincremental y no coinciden con estos valores (p.ej. en el
 * backend real "Care Management" es roleId 5, no 3). Resolver por ID puede
 * etiquetar mal a un usuario o negarle su dashboard. Siempre preferir la key.
 */
const BACKEND_ROLE_MAP = {
  // === Mapeo por roleKey (FUENTE DE VERDAD). Las keys se comparan en minúsculas. ===
  // Administración / sistema (acceso total)
  admin: ROLES.ADMIN,
  super_admin: ROLES.ADMIN,
  it_tech: ROLES.ADMIN,
  // Liderazgo ejecutivo (dashboard ejecutivo, lectura amplia)
  ceo: ROLES.CEO,
  medical_director_cmo: ROLES.CEO,
  regional_manager: ROLES.CEO,
  // Coordinación / gestión de cuidado
  care_management: ROLES.CARE_MANAGEMENT,
  care_coordinator: ROLES.CARE_MANAGEMENT,
  cmp_clinical_managers: ROLES.CARE_MANAGEMENT,
  pediatric_department_manager: ROLES.CARE_MANAGEMENT,
  supervisor_by_location: ROLES.CARE_MANAGEMENT,
  // Salud conductual (dashboard BH dedicado)
  behavioral_health_department: ROLES.BEHAVIORAL_HEALTH,
  // Clínico (PCP) + roles de apoyo clínico/operativo. Aterrizan en el dashboard
  // clínico por defecto; el acceso fino lo refina user.modules (RoleGuard) y el
  // backend. NOTA: los roles clericales (admin_staff, receptionist, back_office,
  // coder_billing) heredan el tier PCP por falta de un tier dedicado — revisar si
  // se requiere restringir (ver ROLE_PERMISSIONS).
  pcp: ROLES.PCP,
  pcp_pediatric: ROLES.PCP,
  medical_assistant: ROLES.PCP,
  admin_staff: ROLES.PCP,
  medical_receptionist_fd: ROLES.PCP,
  back_office: ROLES.PCP,
  coder_billing: ROLES.PCP,

  // === Fallback legacy por ID numérico (coinciden con la tabla actual 1–4; NO
  // confiar para IDs ≥5 — usar siempre roleKey). ===
  "1": ROLES.ADMIN,
  "2": ROLES.PCP,
  "3": ROLES.CARE_MANAGEMENT,
  "4": ROLES.CEO,
};

/**
 * Definición de Permisos por Módulo
 * Formato: "modulo:accion"
 * Acciones: read, write, delete, export, admin
 */
export const PERMISSIONS = {
  // Pacientes
  PATIENTS_READ: "patients:read",
  PATIENTS_WRITE: "patients:write",
  PATIENTS_DELETE: "patients:delete",
  PATIENTS_EXPORT: "patients:export",

  // Care Gaps
  CAREGAPS_READ: "caregaps:read",
  CAREGAPS_WRITE: "caregaps:write",
  CAREGAPS_CLOSE: "caregaps:close",

  // Coding Opportunities
  CODING_READ: "coding:read",
  CODING_WRITE: "coding:write",
  CODING_APPROVE: "coding:approve",

  // Care Plans
  CAREPLANS_READ: "careplans:read",
  CAREPLANS_WRITE: "careplans:write",
  CAREPLANS_APPROVE: "careplans:approve",

  // Clinical Notes
  NOTES_READ: "notes:read",
  NOTES_WRITE: "notes:write",
  NOTES_DELETE: "notes:delete",

  // Specialist Referrals
  REFERRALS_READ: "referrals:read",
  REFERRALS_WRITE: "referrals:write",
  REFERRALS_APPROVE: "referrals:approve",

  // Lab/Phlebotomy
  LAB_READ: "lab:read",
  LAB_WRITE: "lab:write",

  // Reports
  REPORTS_READ: "reports:read",
  REPORTS_EXPORT: "reports:export",

  // AI Features
  AI_USE: "ai:use",
  AI_ADMIN: "ai:admin",

  // Administration
  ADMIN_USERS: "admin:users",
  ADMIN_SETTINGS: "admin:settings",
  ADMIN_AUDIT: "admin:audit",

  // Assignments
  ASSIGNMENTS_READ: "assignments:read",
  ASSIGNMENTS_WRITE: "assignments:write",

  // Documents
  DOCUMENTS_READ: "documents:read",
  DOCUMENTS_WRITE: "documents:write",

  // Support/Debug
  SUPPORT_ACCESS: "support:access",
  SUPPORT_DEBUG: "support:debug",
};

/**
 * Permisos asignados a cada rol
 * TODO: Mover a backend
 */
export const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: ["*"],

  [ROLES.CEO]: ["*"],

  [ROLES.PCP]: [
    PERMISSIONS.PATIENTS_READ,
    PERMISSIONS.PATIENTS_WRITE,
    PERMISSIONS.PATIENTS_EXPORT,
    PERMISSIONS.CAREGAPS_READ,
    PERMISSIONS.CAREGAPS_WRITE,
    PERMISSIONS.CAREGAPS_CLOSE,
    PERMISSIONS.CODING_READ,
    PERMISSIONS.CODING_WRITE,
    PERMISSIONS.NOTES_READ,
    PERMISSIONS.NOTES_WRITE,
    PERMISSIONS.REFERRALS_READ,
    PERMISSIONS.REFERRALS_WRITE,
    PERMISSIONS.REFERRALS_APPROVE,
    PERMISSIONS.LAB_READ,
    PERMISSIONS.LAB_WRITE,
    PERMISSIONS.REPORTS_READ,
    PERMISSIONS.AI_USE,
    PERMISSIONS.ASSIGNMENTS_READ,
    PERMISSIONS.ASSIGNMENTS_WRITE,
    PERMISSIONS.DOCUMENTS_READ,
    PERMISSIONS.DOCUMENTS_WRITE,
    PERMISSIONS.SUPPORT_ACCESS,
  ],

  [ROLES.CARE_MANAGEMENT]: [
    PERMISSIONS.PATIENTS_READ,
    PERMISSIONS.PATIENTS_WRITE,
    PERMISSIONS.CAREGAPS_READ,
    PERMISSIONS.CAREGAPS_WRITE,
    PERMISSIONS.CAREGAPS_CLOSE,
    PERMISSIONS.CAREPLANS_READ,
    PERMISSIONS.CAREPLANS_WRITE,
    PERMISSIONS.NOTES_READ,
    PERMISSIONS.NOTES_WRITE,
    PERMISSIONS.REFERRALS_READ,
    PERMISSIONS.REFERRALS_WRITE,
    PERMISSIONS.REPORTS_READ,
    PERMISSIONS.AI_USE,
    PERMISSIONS.DOCUMENTS_READ,
    PERMISSIONS.DOCUMENTS_WRITE,
  ],

  // Salud conductual: acceso clínico de coordinación (similar a Care Management).
  [ROLES.BEHAVIORAL_HEALTH]: [
    PERMISSIONS.PATIENTS_READ,
    PERMISSIONS.PATIENTS_WRITE,
    PERMISSIONS.CAREPLANS_READ,
    PERMISSIONS.CAREPLANS_WRITE,
    PERMISSIONS.NOTES_READ,
    PERMISSIONS.NOTES_WRITE,
    PERMISSIONS.REFERRALS_READ,
    PERMISSIONS.REFERRALS_WRITE,
    PERMISSIONS.REPORTS_READ,
    PERMISSIONS.AI_USE,
    PERMISSIONS.DOCUMENTS_READ,
    PERMISSIONS.DOCUMENTS_WRITE,
  ],

  [ROLES.GUEST]: [],
};

/**
 * Permisos requeridos por ruta
 * Usado por ProtectedRoute para validar acceso
 */
export const ROUTE_PERMISSIONS = {
  "/Main": null, // Accesible para todos los autenticados
  "/PatientMap": PERMISSIONS.PATIENTS_READ,
  "/ClinicalInfo": PERMISSIONS.PATIENTS_READ,
  "/care-gaps": PERMISSIONS.CAREGAPS_READ,
  "/coding-opportunities": PERMISSIONS.CODING_READ,
  "/Careplan": PERMISSIONS.CAREPLANS_READ,
  "/specialist-referrals": PERMISSIONS.REFERRALS_READ,
  "/LabPhlebotomy": PERMISSIONS.LAB_READ,
  "/ClinicalNoteAi": PERMISSIONS.AI_USE,
  "/support": PERMISSIONS.SUPPORT_ACCESS,
  "/assignments": PERMISSIONS.ASSIGNMENTS_READ,
  "/profile": null, // Todos pueden ver su perfil
  "/library": null, // Accesible para todos los autenticados
};

export const PermissionsProvider = ({ children }) => {
  const { user } = useContext(AuthContext);

  /**
   * Obtener rol normalizado del usuario
   */
  const userRole = useMemo(() => {
    // 1) Preferir el roleKey del backend (estable, independiente del ID de DB).
    const roleKey = user?.activeRole?.key;
    if (roleKey) {
      const byKey = BACKEND_ROLE_MAP[String(roleKey).toLowerCase()];
      if (byKey) return byKey;
    }
    // 2) Fallback legacy: mapear por el ID numérico (poco confiable).
    if (!user?.rol) return ROLES.GUEST;
    const backendRole = String(user.rol).toLowerCase();
    return BACKEND_ROLE_MAP[backendRole] || ROLES.GUEST;
  }, [user?.activeRole?.key, user?.rol]);

  /**
   * Obtener permisos del usuario según su rol
   */
  const userPermissions = useMemo(() => {
    return ROLE_PERMISSIONS[userRole] || [];
  }, [userRole]);

  /**
   * Verificar si el usuario tiene un permiso específico
   */
  const hasPermission = useCallback(
    (permission) => {
      if (!permission) return true; // Sin permiso requerido = acceso libre
      if (userPermissions.includes("*")) return true; // Admin tiene todo
      return userPermissions.includes(permission);
    },
    [userPermissions]
  );

  /**
   * Verificar si el usuario tiene ALGUNO de los permisos (OR)
   */
  const hasAnyPermission = useCallback(
    (permissions) => {
      if (!permissions || permissions.length === 0) return true;
      if (userPermissions.includes("*")) return true;
      return permissions.some((p) => userPermissions.includes(p));
    },
    [userPermissions]
  );

  /**
   * Verificar si el usuario tiene TODOS los permisos (AND)
   */
  const hasAllPermissions = useCallback(
    (permissions) => {
      if (!permissions || permissions.length === 0) return true;
      if (userPermissions.includes("*")) return true;
      return permissions.every((p) => userPermissions.includes(p));
    },
    [userPermissions]
  );

  /**
   * Verificar si el usuario puede acceder a una ruta
   */
  const canAccessRoute = useCallback(
    (path) => {
      const requiredPermission = ROUTE_PERMISSIONS[path];
      return hasPermission(requiredPermission);
    },
    [hasPermission]
  );

  /**
   * Verificar si es admin
   */
  const isAdmin = useMemo(() => {
    return userRole === ROLES.ADMIN || userRole === ROLES.CEO || userPermissions.includes("*");
  }, [userRole, userPermissions]);

  const value = useMemo(
    () => ({
      // Estado
      userRole,
      userPermissions,
      isAdmin,
      // Funciones de verificación
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      canAccessRoute,
      // Constantes exportadas
      ROLES,
      PERMISSIONS,
      ROUTE_PERMISSIONS,
    }),
    [
      userRole,
      userPermissions,
      isAdmin,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      canAccessRoute,
    ]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

/**
 * Hook para usar el contexto de permisos
 */
export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error("usePermissions debe usarse dentro de PermissionsProvider");
  }
  return context;
};

/**
 * HOC para proteger componentes por permiso
 */
export const withPermission = (WrappedComponent, requiredPermission) => {
  return function PermissionWrapper(props) {
    const { hasPermission } = usePermissions();

    if (!hasPermission(requiredPermission)) {
      return null; // O un componente de "Sin acceso"
    }

    return <WrappedComponent {...props} />;
  };
};

export default PermissionsContext;
